/**
 * The shell: the navigation rail, the phone drawer, and the container pages mount into.
 *
 * The port of `MainPage.xaml` and `ShellViewModel`. The responsive design is the same one and for the
 * same reasons - an overlay drawer rather than a reflow, so the content area never has to know which
 * mode the shell is in - but it is built differently in two ways worth knowing:
 *
 *   * ONE RAIL SERVES BOTH MODES, as in the XAML, but by switching `dock` rather than by spanning two
 *     grid columns and relying on ZIndex. Wide: `dock: 'left'`, so the docking pass carves it out of
 *     the page and the content fills the rest. Narrow: `dock: 'none'` with absolute bounds, so it
 *     overlays. It is added to the page LAST, which is what puts it on top: Webwin paints children in
 *     order and hit-tests them in reverse, so "last" means both drawn over and clicked first.
 *   * THE WIDTH SIGNAL IS A RESIZE EVENT, not a poll. The OpenSilver shell had to poll
 *     `window.innerWidth` every 800ms because `Host.Content.Resized` never fired in that host;
 *     Webwin's desktop runs a ResizeObserver on the canvas and a DPR watcher beside it, so `resize`
 *     is reliable and the timer is gone.
 */
import {
  insets,
  NotificationHost,
  Page,
  Panel,
  ScrollViewer,
  type Desktop,
  type FontSpec,
  type RenderContext,
  type StackPanel,
  type Theme
} from 'webwin';
import * as auth from './api/auth.js';
import * as hostAccess from './api/hostAccess.js';
import * as hub from './api/hub.js';
import * as settings from './api/settings.js';
import { request } from './api/http.js';
import { HostPermission, TenantLimits, TicketSource, type AppVersionInfo, type LockedAccountDto, type TicketDto } from './contracts.gen.js';
import * as router from './router.js';
import { onLockoutCount } from './lockoutCount.js';
import { attachTheme, currentChoice, onChoiceChanged, setChoice, type ThemeChoice } from './theme/store.js';
import { crm } from './theme/CrmTheme.js';
import { Card } from './ui/Card.js';
import { Disposables } from './ui/Disposables.js';
import { NavButton } from './ui/NavButton.js';
import { CrmScroller } from './ui/Scroller.js';
import { CrmLabel } from './ui/Text.js';
import { FONT, GAP, hstack, NARROW_WIDTH, relayout, vstack } from './ui/layout.js';
import { isStale, reloadForUpdate, stampDocumentTitle, VERSION_LABEL } from './version.js';
import { auditPage } from './pages/audit.js';
import { catalogPage } from './pages/catalog.js';
import { changePasswordPage } from './pages/change-password.js';
import { customerDetailPage } from './pages/customer-detail.js';
import { customersPage } from './pages/customers.js';
import { dashboardPage } from './pages/dashboard.js';
import { deviceDetailPage } from './pages/device-detail/index.js';
import { devicesPage } from './pages/devices.js';
import { expensesPage } from './pages/expenses.js';
import { leadsPage } from './pages/leads.js';
import { leadDetailPage } from './pages/lead-detail.js';
import { marketingPage } from './pages/marketing.js';
import { campaignDetailPage } from './pages/campaign-detail.js';
import { invoiceDetailPage } from './pages/invoice-detail.js';
import { invoicesPage } from './pages/invoices.js';
import { loginPage } from './pages/login.js';
import { notFoundPage } from './pages/not-found.js';
import { hostDashboardPage } from './pages/host-dashboard.js';
import { hostBackupsPage } from './pages/host-backups.js';
import { hostBillingPage } from './pages/host-billing.js';
import { hostUsersPage } from './pages/host-users.js';
import { organizationsPage } from './pages/organizations.js';
import { serverUpdatesPage } from './pages/server-updates.js';
import { profitLossPage } from './pages/profit-loss.js';
import { reportsPage } from './pages/reports.js';
import { billingPage } from './pages/billing.js';
import { monitorDetailPage } from './pages/monitor-detail.js';
import { networkPage } from './pages/network.js';
import { securityPage } from './pages/security.js';
import { usersPage } from './pages/users.js';
import { settingsPage } from './pages/settings.js';
import { softwarePage } from './pages/software.js';
import { ticketDetailPage } from './pages/ticket-detail.js';
import { ticketsPage } from './pages/tickets.js';

const RAIL_WIDTH = 220;
const TOP_BAR_HEIGHT = 48;

/** The sidebar's lockout count. Why a minute and not twenty: see `#checkLockouts`. */
const LOCKOUT_POLL_MS = 60_000;

/**
 * How often the running client re-asks what the server is serving.
 *
 * Generous on purpose: a deployment is a rare event and this is a poll on every open browser, so
 * noticing twenty minutes late costs nothing while asking every minute costs a request per tab per
 * minute for ever. Signing in checks at once, which covers the case that actually matters - somebody
 * opening the CRM after an upgrade.
 */
const VERSION_POLL_MS = 20 * 60_000;

interface NavEntry {
  readonly label: string;
  readonly path: string;
  readonly adminOnly?: boolean;
  /** A host page that needs this host permission. */
  readonly needs?: HostPermission;
}

const NAV: readonly NavEntry[] = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Customers', path: '/customers' },
  { label: 'Leads', path: '/leads' },
  { label: 'Marketing', path: '/marketing' },
  { label: 'Devices', path: '/devices' },
  { label: 'Tickets', path: '/tickets' },
  { label: 'Invoices', path: '/invoices' },
  { label: 'Catalog', path: '/catalog' },
  { label: 'Software', path: '/software' },
  { label: 'Expenses', path: '/expenses' },
  { label: 'Reports', path: '/reports' },
  { label: 'Profit & Loss', path: '/profit-loss' },
  // Everything behind these is refused to a technician server-side, so the entry is hidden rather
  // than disabled: an entry that opens a page saying "not for you" is worse than no entry.
  { label: 'Users', path: '/users', adminOnly: true },
  { label: 'Security', path: '/security', adminOnly: true },
  { label: 'Audit log', path: '/audit', adminOnly: true },
  // Visible to every staff user: the values are read-only for a technician, and an error naming a
  // limit is only actionable next to the number.
  { label: 'Settings', path: '/settings' }
];

/** A host operator's own pages, outside every organization. */
const HOST_NAV: readonly NavEntry[] = [
  { label: 'Dashboard', path: '/host' },
  { label: 'Organizations', path: '/organizations' },
  { label: 'Backups', path: '/host-backups', needs: HostPermission.ManageOrganizations },
  { label: 'Users', path: '/host-users', needs: HostPermission.ManageUsers },
  { label: 'Billing', path: '/host-billing', needs: HostPermission.ManageBilling },
  { label: 'Server updates', path: '/server-updates', needs: HostPermission.ManageServer }
];

/**
 * The application's root surface.
 *
 * Webwin's `Page` takes its background from the theme's `windowBg`, which in this palette is the CARD
 * colour - so the page and every card on it would be the same white and the cards would disappear.
 * Repainting from `pageBackground` here rather than passing a fixed colour at construction is what
 * keeps it following a theme switch: the CRM's page is a dimmer surface than its cards in both
 * themes, and that contrast is the whole visual structure of every screen.
 */
class PageRoot extends Page {
  protected override onPaint(rc: RenderContext, theme: Theme): void {
    this.background = crm(theme).pageBackground;
    super.onPaint(rc, theme);
  }
}

export class Shell {
  readonly page: Page;
  /** Where the router mounts a page. */
  readonly pageHost: Panel;

  readonly #desktop: Desktop;
  readonly #rail: Card;
  readonly #scrim: Panel;
  readonly #topBar: Card;
  readonly #navHost: StackPanel;
  #navScroller!: CrmScroller;
  readonly #signedInHost: StackPanel;
  readonly #userLabel: CrmLabel;
  readonly #updateChips: Card[] = [];
  readonly #updateLabels: CrmLabel[] = [];
  readonly #navButtons = new Map<string, NavButton>();
  /** Shell-lifetime timers. Never disposed: there is exactly one shell for the life of the app. */
  readonly #timers = new Disposables();

  #narrow = false;
  #drawerOpen = false;
  #lockedCount = 0;
  #servedVersion = '';
  /** Tickets raised from the agent that this person has not opened, or seen in the Tickets list. */
  readonly #unseenTickets = new Set<number>();
  #toasts: NotificationHost | null = null;

  constructor(desktop: Desktop) {
    this.#desktop = desktop;
    // The Security page reports the exact count when it loads or releases one; see lockoutCount.ts.
    onLockoutCount(count => this.#setLockedCount(count));
    this.page = new PageRoot();

    this.pageHost = new Panel({ dock: 'fill', style: 'none' });
    this.#topBar = this.#buildTopBar();

    const content = new Panel({ dock: 'fill', style: 'none' });
    content.controls.add(this.#topBar);
    content.controls.add(this.pageHost);

    // Added before the rail so the rail paints over it, and after the content so a tap beside the
    // open drawer closes it rather than reaching the page underneath.
    this.#scrim = new Panel({
      style: 'none',
      background: { r: 0, g: 0, b: 0, a: 0.4 },
      visible: false
    });
    this.#scrim.on('click', () => this.closeDrawer());

    this.#navHost = vstack({ spacing: 2, padding: insets(GAP.md, 0, GAP.md, GAP.md) });
    this.#userLabel = new CrmLabel({
      text: '',
      font: FONT.bold,
      role: 'sidebar',
      autoHeight: true,
      hAlign: 'stretch'
    });
    this.#signedInHost = this.#buildSignedInBlock();
    this.#rail = this.#buildRail();

    this.page.controls.add(content);
    this.page.controls.add(this.#scrim);
    this.page.controls.add(this.#rail);

    // Docking handles the rail and the content; the drawer's absolute bounds and the mode itself have
    // to be recomputed by hand.
    this.page.on('resize', () => this.#applyMode());
    this.#applyMode();
  }

  // ---------------------------------------------------------------------
  //  Construction
  // ---------------------------------------------------------------------

  /**
   * An amber "a newer build is deployed" chip.
   *
   * Two are built - one on the rail, one on the top bar - and that repetition is deliberate: on a
   * phone the rail is a drawer, so an update offered only inside it is one nobody sees unless they
   * were already going to navigate, which is exactly the person least likely to notice they are on a
   * stale build.
   */
  #buildUpdateChip(): Card {
    const label = new CrmLabel({
      text: 'Update now',
      font: FONT.small,
      role: 'warn',
      autoWidth: true,
      autoHeight: true,
      wrap: false
    });
    const chip = new Card({
      tint: 'warn',
      radius: 4,
      bordered: false,
      padding: insets(10, 5),
      autoWidth: true,
      autoHeight: true,
      visible: false,
      cursor: 'pointer',
      tooltip:
        'A newer CRM has been deployed. This reloads the app to pick it up - anything unsaved on the current page is lost.'
    });
    chip.controls.add(label);
    chip.on('click', () => void reloadForUpdate());
    this.#updateChips.push(chip);
    this.#updateLabels.push(label);
    return chip;
  }

  #buildTopBar(): Card {
    const bar = new Card({
      tint: 'sidebar',
      radius: 0,
      bordered: false,
      dock: 'top',
      height: TOP_BAR_HEIGHT,
      padding: insets(GAP.xs, GAP.xs, GAP.md, GAP.xs),
      visible: false
    });

    const row = hstack({ spacing: GAP.sm, dock: 'fill', autoWidth: false, autoHeight: false });
    // 44px square: the minimum comfortable touch target, as the XAML notes.
    row.controls.add(new NavButton({ text: '☰', width: 44, height: 44, onClick: () => this.toggleDrawer() }));
    const title = hstack({ spacing: GAP.sm, cursor: 'pointer', tooltip: 'Dashboard' });
    title.controls.add(railText('Jetsonsoft CRM', FONT.bold, 'sidebar'));
    title.controls.add(railText(VERSION_LABEL, FONT.small, 'sidebarMuted'));
    title.on('click', () => this.#navigate('/dashboard'));
    row.controls.add(title);
    row.controls.add(this.#buildUpdateChip());
    bar.controls.add(row);
    return bar;
  }

  #buildRail(): Card {
    const rail = new Card({
      tint: 'sidebar',
      radius: 0,
      bordered: false,
      dock: 'left',
      width: RAIL_WIDTH,
      padding: insets(0)
    });

    // The product name, the running build, and the update chip.
    //
    // The version has a permanent corner of the chrome rather than a Help/About box: the first thing
    // a support conversation needs is which version the person is actually looking at, and a stale
    // browser is the one case where nobody can be trusted to know.
    const brand = vstack({
      spacing: 0,
      padding: insets(GAP.lg, 20, GAP.lg, GAP.lg),
      dock: 'top',
      cursor: 'pointer',
      tooltip: 'Dashboard'
    });
    // The product name is the way home, as it is on most web apps.
    brand.on('click', () => this.#navigate('/dashboard'));
    brand.controls.add(railText('Jetsonsoft', { family: FONT.h1.family, size: 20, weight: 700 }, 'sidebar'));
    const versionRow = hstack({ spacing: GAP.sm });
    versionRow.controls.add(railText('CRM', FONT.small, 'sidebarMuted'));
    versionRow.controls.add(railText(VERSION_LABEL, FONT.small, 'sidebarMuted'));
    brand.controls.add(versionRow);
    brand.controls.add(this.#buildUpdateChip());

    // The nav scrolls on its own: a short phone-landscape viewport must not push Sign out off the
    // bottom with no way to reach it. `dock: 'fill'` because a ScrollViewer does not measure itself -
    // given an auto height it would report its 160px constructor default and the rail would be nearly
    // empty. The stack inside it stretches and measures, which is what gives the scroll range.
    const navScroller = new CrmScroller({
      dock: 'fill',
      verticalScroll: 'auto',
      horizontalScroll: 'never',
      showBorder: false,
      zoomEnabled: false
    });
    navScroller.content.controls.add(this.#navHost);
    this.#navScroller = navScroller;

    rail.controls.add(brand);
    // Added before the filler so docking carves its edge first - the bottom block keeps its height
    // and the scroller takes what is left.
    rail.controls.add(this.#signedInHost);
    rail.controls.add(navScroller);
    return rail;
  }

  #buildSignedInBlock(): StackPanel {
    const block = vstack({
      spacing: GAP.xs,
      padding: insets(GAP.lg, GAP.md, GAP.lg, GAP.lg),
      dock: 'bottom',
      visible: false
    });
    block.controls.add(this.#userLabel);
    // The one place a password is set in the whole product, which is why the knowledge base and the
    // forum link here rather than carrying their own form.
    block.controls.add(
      new NavButton({ text: 'Change password', height: 30, onClick: () => this.#navigate('/change-password') })
    );
    block.controls.add(new NavButton({ text: 'Sign out', height: 30, onClick: () => void this.#signOut() }));
    return block;
  }

  /** Rebuilt on every session change, because the admin-only entries depend on the role. */
  #buildNav(): void {
    this.#navHost.controls.clear();
    this.#navButtons.clear();

    // A host operator outside every organization has the host's pages and nothing else: every
    // organization's own page would be refused to a host token.
    if (auth.isHostOnly()) {
      for (const entry of HOST_NAV) {
        if (entry.needs !== undefined && !hostAccess.has(entry.needs)) continue;
        const nav = new NavButton({ text: entry.label, onClick: () => this.#navigate(entry.path) });
        this.#navHost.controls.add(nav);
        this.#navButtons.set(entry.path, nav);
      }
      this.#navHost.controls.add(this.#buildAppearanceRow());
      this.#markSelected();
      this.#refreshNavScroll();
      return;
    }

    // Working inside an organization as a host operator: say which, every time the rail is seen,
    // with the way back beside it - an administrator session in somebody else's organization must
    // never be mistaken for one's own.
    const entered = auth.enteredTenant();
    if (entered) {
      const banner = vstack({ spacing: GAP.xs, padding: insets(GAP.md, GAP.xs, GAP.md, GAP.md) });
      banner.controls.add(railText('Working in', FONT.tiny, 'sidebarMuted'));
      banner.controls.add(
        new CrmLabel({ text: entered.name, font: FONT.bold, role: 'sidebar', autoHeight: true, hAlign: 'stretch' })
      );
      banner.controls.add(
        new NavButton({ text: '← All organizations', height: 30, onClick: () => this.#leaveTenant() })
      );
      this.#navHost.controls.add(banner);
    }

    const admin = isAdmin();
    for (const entry of NAV) {
      if (entry.adminOnly === true && !admin) continue;
      const nav = new NavButton({ text: entry.label, onClick: () => this.#navigate(entry.path) });
      this.#navHost.controls.add(nav);
      this.#navButtons.set(entry.path, nav);
    }

    // The knowledge base and the forum are separate apps, so these open a tab rather than navigate.
    // Same-origin paths, never a guessed hostname: the path always exists, because it is where those
    // bundles are served from.
    this.#navHost.controls.add(
      new NavButton({ text: 'Knowledge base ↗', onClick: () => this.#openTab(knowledgeBasePath(), 'jetsoncrm-kb') })
    );
    this.#navHost.controls.add(
      new NavButton({ text: 'Forum ↗', onClick: () => this.#openTab('/forum/', 'jetsoncrm-forum') })
    );

    this.#navHost.controls.add(this.#buildAppearanceRow());
    this.#markSelected();
    this.#refreshNavScroll();
  }

  /**
   * A ScrollViewer decides whether it needs a bar only when it is told to. Rebuilding the nav grows
   * the content without telling it, so on a short window the list was cut off at Security with no
   * bar and nothing to scroll.
   */
  #refreshNavScroll(): void {
    relayout(this.#navHost);
    this.#navScroller.updateScrollBars();
    this.page.invalidate();
  }

  /**
   * The theme switch, in the rail.
   *
   * Settings will keep the authoritative control - it is a stored preference like any other - but the
   * rail is where somebody reaches for it, and during the coexistence period it is also the quickest
   * way to confirm that both interfaces really do agree on the shared key.
   */
  #buildAppearanceRow(): StackPanel {
    const row = vstack({ spacing: GAP.xs, padding: insets(GAP.md, GAP.lg, GAP.md, 0) });
    row.controls.add(railText('Appearance', FONT.tiny, 'sidebarMuted'));

    const choices: ReadonlyArray<readonly [ThemeChoice, string]> = [
      ['light', 'Light'],
      ['dark', 'Dark'],
      ['auto', 'Match system']
    ];
    const buttons = new Map<ThemeChoice, NavButton>();
    const mark = (): void => {
      const active = currentChoice();
      for (const [choice, nav] of buttons) nav.selected = choice === active;
      this.page.invalidate();
    };
    for (const [choice, label] of choices) {
      const nav = new NavButton({
        text: label,
        height: 30,
        onClick: () => setChoice(choice)
      });
      buttons.set(choice, nav);
      row.controls.add(nav);
    }
    // Settings carries the same choice, and another tab can move it.
    onChoiceChanged(mark);
    mark();
    return row;
  }

  // ---------------------------------------------------------------------
  //  Responsive mode
  // ---------------------------------------------------------------------

  #applyMode(): void {
    const { width, height } = this.page.clientSize;
    if (width <= 0) return;

    const narrow = width < NARROW_WIDTH;
    if (narrow !== this.#narrow) {
      this.#narrow = narrow;
      // Rotating a phone into landscape can cross the threshold with the drawer open, and leaving it
      // open in wide mode would show the rail twice.
      if (!narrow) this.#drawerOpen = false;
    }

    this.#topBar.visible = narrow;

    if (narrow) {
      this.#rail.dock = 'none';
      this.#rail.visible = this.#drawerOpen;
      this.#rail.setBounds(0, 0, RAIL_WIDTH, height);
      this.#scrim.visible = this.#drawerOpen;
      this.#scrim.setBounds(0, 0, width, height);
    } else {
      this.#rail.dock = 'left';
      this.#rail.visible = true;
      this.#scrim.visible = false;
    }

    this.page.layout();
    this.#navScroller.updateScrollBars();
  }

  toggleDrawer(): void {
    this.#drawerOpen = !this.#drawerOpen;
    this.#applyMode();
  }

  closeDrawer(): void {
    if (!this.#drawerOpen) return;
    this.#drawerOpen = false;
    this.#applyMode();
  }

  /**
   * Navigates and closes the drawer.
   *
   * Every rail action closes it, for the reason the XAML gives: on a phone the menu overlays the
   * content, and a menu that stays open over the page it just navigated to reads as the navigation
   * not having happened.
   */
  #navigate(path: string): void {
    this.closeDrawer();
    router.navigate(path);
  }

  #openTab(path: string, handle: string): void {
    this.closeDrawer();
    // Called inside the click, so it is a real user gesture and the pop-up is not blocked.
    window.open(`${window.location.origin}${path}`, handle);
  }

  /** Marks the rail entry whose page is showing. */
  #markSelected(): void {
    const current = router.currentRoute();
    const route = current === '/' ? '/dashboard' : current;
    for (const [path, nav] of this.#navButtons) {
      nav.selected = route === path || route.startsWith(`${path}/`);
    }
    this.page.invalidate();
  }

  // ---------------------------------------------------------------------
  //  Session and polls
  // ---------------------------------------------------------------------

  /**
   * Called once.
   *
   * Everything downstream hangs off `onSessionChanged` rather than being called after each sign-in:
   * dev sign-in, a restored session, the login page and a forced password change are four separate
   * paths into the app, and this way none of them is the one that was forgotten.
   */
  start(): void {
    attachTheme(this.#desktop);
    router.mount(this.pageHost, this.#desktop);
    router.setGuard(path => {
      if (!auth.isSignedIn() || path === '/login' || path === '/change-password') return null;
      const hostPage = HOST_NAV.some(e => e.path === path);
      if (auth.isHostOnly()) {
        if (!hostPage) return '/host';
        // Before the operator's access has loaded, let the page ask: the server refuses it with a sentence.
        const needs = HOST_NAV.find(e => e.path === path)?.needs;
        return needs !== undefined && hostAccess.isLoaded() && !hostAccess.has(needs) ? '/host' : null;
      }
      return hostPage ? '/dashboard' : null;
    });
    // Every navigation, not just Back and Forward: see router.onNavigated.
    router.onNavigated(() => {
      this.#markSelected();
      this.#markTicketsSeen();
    });

    // Above every page, in the corner of the screen rather than of whatever page is showing.
    this.#toasts = this.#desktop.addForeground(
      new NotificationHost({ corner: 'bottom-right', maxWidth: 340, maxVisible: 4 })
    );
    // Subscribed once: the hub keeps its handlers for the life of the app. A ticket arriving while
    // nobody is signed in is ignored here and still emailed by the server.
    hub.on('TicketCreated', ticket => this.#onTicketCreated(ticket));

    auth.onSessionChanged(() => this.#onSession());
    this.#onSession();

    this.#timers.interval(() => void this.#checkLockouts(), LOCKOUT_POLL_MS);
    this.#timers.interval(() => void this.#checkVersion(), VERSION_POLL_MS);
  }

  /** Re-marks the rail after a navigation the shell did not start. */
  syncSelection(): void {
    this.#markSelected();
  }

  /** Whether the previous session was inside an organization, so its end can be told from a sign-in. */
  #wasInOrganization = false;

  #onSession(): void {
    const signedIn = auth.isSignedIn();
    this.#signedInHost.visible = signedIn;
    this.#navHost.visible = signedIn;
    this.#userLabel.text = auth.currentUser()?.displayName ?? '';
    this.#buildNav();

    // A host operator's own session reads no organization's settings and joins no organization's
    // hub; entering or leaving one is a new session as far as both are concerned.
    if (signedIn && auth.isHostOnly()) {
      // What this operator may do decides which host entries the rail offers; it is re-read on every
      // host session so a change another operator made shows after leaving an organization.
      void hostAccess.load().then(() => {
        if (auth.isHostOnly()) this.#buildNav();
      });
      // An organization session that ended underneath a page - the operator was unassigned or
      // disabled, or the organization was suspended - leaves that page behind with an error on it.
      if (this.#wasInOrganization && !HOST_NAV.some(e => e.path === router.currentRoute())) router.navigate('/organizations', true);
      this.#setLockedCount(0);
      settings.reset();
      void hub.disconnect();
      void this.#checkVersion();
    } else if (signedIn) {
      void this.#checkLockouts();
      void this.#checkVersion();
      // The instance settings - transfer caps, listing caps, event-log caps - are fetched once per
      // session and read synchronously from inside layout code, so they are loaded here rather than
      // by whichever page happens to need one first. Failure leaves the shipped defaults in force.
      void settings.load();
      // Every other hub use is a page's; this is the one that has to be live whatever page is open.
      void hub.ensureConnected();
    } else {
      this.#setLockedCount(0);
      this.#unseenTickets.clear();
      this.#showUnseenTickets();
      this.#toasts?.clear();
      hostAccess.reset();
      // Dropped on sign-out, so the next session reads its own deployment's numbers rather than the
      // previous one's.
      settings.reset();
    }
    this.#wasInOrganization = signedIn && auth.enteredTenant() !== null;
    this.#applyMode();
  }

  #leaveTenant(): void {
    this.closeDrawer();
    auth.leaveTenant();
    router.navigate('/organizations', true);
  }

  async #signOut(): Promise<void> {
    await auth.signOut();
    router.clearCurrent();
    router.navigate('/login', true);
  }

  /**
   * The lockout count on the Security entry.
   *
   * Polled every minute rather than every twenty: a lockout lasts two hours by default and the point
   * of the label is that somebody finds out while it is still happening, so the version check's
   * cadence would routinely show it for the last third of its life. It is one indexed count, on an
   * admin's browser only.
   *
   * A failure leaves the last count alone rather than clearing it - a momentary outage is not evidence
   * that the lockouts went away.
   */
  async #checkLockouts(): Promise<void> {
    if (!isAdmin()) {
      // A technician signing in after an admin must not keep the admin's count on screen.
      this.#setLockedCount(0);
      return;
    }
    try {
      const locked = await request<LockedAccountDto[]>('/api/security/lockouts');
      this.#setLockedCount(locked?.length ?? 0);
    } catch {
      /* see above */
    }
  }

  /** Re-reads the count now - for a page that has just released one. */
  refreshLockoutCount(): void {
    void this.#checkLockouts();
  }

  #setLockedCount(count: number): void {
    if (this.#lockedCount === count) return;
    this.#lockedCount = count;
    const nav = this.#navButtons.get('/security');
    if (nav) {
      nav.text = count > 0 ? `Security (${count})` : 'Security';
      nav.attention = count > 0;
    }
    this.page.invalidate();
  }

  /**
   * A customer raised a ticket - from the agent tray on a managed PC, or by email.
   *
   * The toast stays until it is clicked or dismissed: a ticket is somebody waiting for an answer, and
   * one that timed out while the technician was in another tab would be a notification nobody saw.
   * The count on the Tickets entry and in the tab title is what is left after the toast is gone.
   */
  #onTicketCreated(ticket: TicketDto): void {
    if (!auth.isSignedIn()) return;
    const route = router.currentRoute();
    if (route !== '/tickets' && route !== `/tickets/${ticket.id}`) {
      this.#unseenTickets.add(ticket.id);
      this.#showUnseenTickets();
    }

    this.#toasts?.show({
      content: [
        {
          text: `New ticket #${ticket.id} ${ticket.source === TicketSource.Email ? 'by email' : 'from the agent'}
`,
          font: FONT.bold
        },
        { text: ticket.title }
      ],
      seconds: 0,
      accent: ticket.priority >= 2 ? 'warning' : 'info',
      onClick: () => this.#navigate(`/tickets/${ticket.id}`)
    });
  }

  /** The list shows every ticket; a ticket's own page shows that one. */
  #markTicketsSeen(): void {
    if (this.#unseenTickets.size === 0) return;
    const route = router.currentRoute();
    if (route === '/tickets') this.#unseenTickets.clear();
    else if (route.startsWith('/tickets/')) this.#unseenTickets.delete(Number(route.slice('/tickets/'.length)));
    else return;
    this.#showUnseenTickets();
  }

  #showUnseenTickets(): void {
    const count = this.#unseenTickets.size;
    const nav = this.#navButtons.get('/tickets');
    if (nav) {
      nav.text = count > 0 ? `Tickets (${count} new)` : 'Tickets';
      nav.attention = count > 0;
    }
    stampDocumentTitle(count);
    this.page.invalidate();
  }

  /**
   * Asks the server what it is serving.
   *
   * Any failure is silent and leaves the chip alone: a CRM that is briefly unreachable is not a reason
   * to tell somebody their client is out of date, and certainly not a reason to offer a reload that
   * would land on a sign-in screen.
   */
  async #checkVersion(): Promise<void> {
    if (!auth.isSignedIn()) return;
    try {
      const info = await request<AppVersionInfo>('/api/app/version');
      this.#servedVersion = info?.version ?? '';
    } catch {
      return;
    }
    const stale = isStale(this.#servedVersion);
    // Names the build on offer: "Update to v1.2.0" says what pressing it gets you.
    const label = stale && this.#servedVersion.length > 0 ? `Update to v${this.#servedVersion}` : 'Update now';
    for (const l of this.#updateLabels) l.text = label;
    for (const chip of this.#updateChips) chip.visible = stale;
    this.page.invalidate();
  }
}

function isAdmin(): boolean {
  return auth.currentUser()?.role?.toLowerCase() === 'admin';
}

/** The knowledge base for this organization. It remembers the organization from here, so a reader
 *  who signs out there still sees this organization's public articles rather than the default one's. */
function knowledgeBasePath(): string {
  const tid = auth.tenantId();
  return tid !== null && tid !== TenantLimits.DefaultTenantId ? `/kb/?tenant=${tid}` : '/kb/';
}

/** A label on the dark rail: measured, never wrapped, never stretched. */
function railText(content: string, font: FontSpec, role: 'sidebar' | 'sidebarMuted'): CrmLabel {
  return new CrmLabel({ text: content, font, role, autoWidth: true, autoHeight: true, wrap: false });
}

/** Registers every route. First match wins, so a literal route goes before a pattern. */
export function registerRoutes(): void {
  router.route('/login', loginPage);
  router.route('/change-password', changePasswordPage);

  router.route('/', dashboardPage);
  router.route('/dashboard', dashboardPage);
  router.route('/customers', customersPage);
  router.route('/customers/:id', customerDetailPage);
  router.route('/leads', leadsPage);
  router.route('/leads/:id', leadDetailPage);
  router.route('/marketing', marketingPage);
  router.route('/marketing/:id', campaignDetailPage);
  router.route('/devices', devicesPage);
  router.route('/devices/:id', deviceDetailPage);
  router.route('/tickets', ticketsPage);
  router.route('/tickets/:id', ticketDetailPage);
  router.route('/invoices', invoicesPage);
  router.route('/invoices/:id', invoiceDetailPage);
  router.route('/billing/:id', billingPage);
  router.route('/catalog', catalogPage);
  router.route('/software', softwarePage);
  router.route('/expenses', expensesPage);
  router.route('/reports', reportsPage);
  router.route('/profit-loss', profitLossPage);
  router.route('/network/:id', networkPage);
  router.route('/monitors/:id', monitorDetailPage);
  router.route('/users', usersPage);
  router.route('/security', securityPage);
  router.route('/audit', auditPage);
  router.route('/settings', settingsPage);
  router.route('/host', hostDashboardPage);
  router.route('/organizations', organizationsPage);
  router.route('/host-users', hostUsersPage);
  router.route('/host-billing', hostBillingPage);
  router.route('/host-backups', hostBackupsPage);
  router.route('/server-updates', serverUpdatesPage);

  router.setNotFound(notFoundPage);
}
